'use client';

import { useMemo, useState } from 'react';

import { useBenzingaEdge } from '@benzinga/edge';
import type { MemberTrade } from '@benzinga/gov-trades';
import type { ColumnDef } from '@benzinga/table';

import { GovernmentTable } from '../../../../src/components/GovTrades';
import { TradesToolbar } from '../securities/_components/TradesToolbar';
import { defaultToolbar, presetToDateFrom } from '../securities/_components/tradesQuery';

const PAGE_SIZE = 10;
const pageButtonClass =
  'flex h-[34px] min-w-[34px] cursor-pointer items-center justify-center rounded-lg border border-[#e3e9f4] bg-white px-2 font-sans text-[13px] font-bold text-bzgray-600 disabled:cursor-default disabled:opacity-50';

interface MemberTradesTableProps {
  columnsDef: ColumnDef<MemberTrade>[];
  rows: MemberTrade[];
  title?: string;
}

export const MemberTradesTable = ({ columnsDef, rows, title }: MemberTradesTableProps) => {
  const { hasEdgeSubscription: hasPermission, isEdgeSubscriptionLoading } = useBenzingaEdge();
  const [toolbar, setToolbar] = useState(defaultToolbar);
  const [page, setPage] = useState(0);

  const filteredRows = useMemo(() => {
    const ticker = toolbar.ticker.trim().toLowerCase();
    const dateFrom = presetToDateFrom(toolbar.datePreset);
    const fromTime = dateFrom ? Date.parse(dateFrom) : null;

    return rows.filter(row => {
      if (ticker && !(row.ticker ?? '').toLowerCase().includes(ticker)) return false;
      if (
        toolbar.transaction &&
        !(row.transaction_type ?? '')
          .toLowerCase()
          .startsWith(toolbar.transaction === 'purchase' ? 'p' : 's')
      )
        return false;
      if (fromTime != null && Date.parse(row.transaction_date) < fromTime) return false;
      return true;
    });
  }, [rows, toolbar]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const visibleRows = hasPermission
    ? filteredRows.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE)
    : filteredRows.slice(0, PAGE_SIZE);

  return (
    <div className="all-trades">
      <TradesToolbar
        disabled={isEdgeSubscriptionLoading || !hasPermission}
        hideChamber
        hideSort
        onChange={next => {
          setToolbar(next);
          setPage(0);
        }}
        showTransaction
        value={toolbar}
      />
      <GovernmentTable
        autoHeight
        columnsDef={columnsDef}
        height={789}
        layout="all-trades"
        rowData={visibleRows}
        title={title}
        variant="light"
      />
      {hasPermission && totalPages > 1 ? (
        <div className="flex items-center justify-start border-t border-[#eff3fa] bg-white p-3.5">
          <div className="flex items-center gap-1.5">
            <button
              aria-label="Previous page"
              className={pageButtonClass}
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
            >
              ‹
            </button>
            <span className="flex h-[34px] min-w-[34px] items-center justify-center rounded-lg border border-[#3f6df6] bg-[#3f6df6] px-2 font-sans text-[13px] font-bold text-white">
              {currentPage + 1}
            </span>
            <button
              aria-label="Next page"
              className={pageButtonClass}
              disabled={currentPage === totalPages - 1}
              onClick={() => setPage(currentPage + 1)}
            >
              ›
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
